import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { appNoticeLinks } from '../../../constants/app-notice-card-links';
import { AgencyModel } from '../../../models/agency.model';
import { AppNotice } from '../../../models/app-notice.model';
import { CompanyModel } from '../../../models/company.model';
import { FlipperService } from '../../../services/flipper/flipper.service';
import { UserModel } from '../../../models/user.model';
import { RoutesService } from '../../../services/routes/routes.service';
import { BillingService } from '../../../services/billing/billing.service';
import { AppNoticesService } from '../../../services/app-notices/app-notices.service';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';

const NOTICE_TITLES: Partial<Record<string, string>> = {
  annual_plans_reminder: 'Annual plan renewal reminder',
  js_not_installed: 'JavaScript snippet not installed',
  account_disabled: 'Account disabled',
  account_closing: 'Account closing',
  trial_pending: 'Trial pending',
  trial: 'In trial',
  lc_trial: 'In trial',
  forms_legacy_upgrade_required: 'Forms upgrade required',
  forms_lite_upgrade_required: 'Forms upgrade required',
  form_capture_limit: 'Form submission limit reached',
  billing: 'Payment failed',
  manual_invoice_late: 'Invoice past due',
  no_trackers: 'No active numbers',
  api_v3: 'API v3 now available',
  no_forms_product: 'Try form tracking',
  risky_swap: 'Numbers swapping too fast',
  number_not_swapping: 'Number Not Swapping',
  snippet_installation: '',
  redundant_plans: 'Redundant plans detected',
  tendlc_registration__high: '10DLC registration required',
  tendlc_registration__high__admin: '10DLC registration required',
  tendlc_registration__mid: '10DLC registration warning',
  tendlc_registration__mid__admin: '10DLC registration warning',
  tendlc_registration__low: '10DLC registration reminder',
  tendlc_registration__low__admin: '10DLC registration reminder',
};

@Component({
  selector: 'core-app-notice-message',
  templateUrl: './app-notice-message.component.html',
  styleUrls: ['./app-notice-message.component.scss'],
  standalone: false,
})
export class AppNoticeMessageComponent implements OnInit, OnDestroy {
  @Input() public appNotice: AppNotice;
  @Input() public agency: AgencyModel;
  @Input() public company: CompanyModel;
  @Input() public user: UserModel;
  @Input() public withAlertTitle = false;
  @Input() public withPadding = false;
  @Input() public withMockLinks = false;
  @Input() public withMessageVariant = false;

  public routes;
  public showCCLinkToPending$ = this.flipperService.flipperEnabled(
    'show_cc_link_to_pending'
  );
  public appNoticeLinks = appNoticeLinks;

  public annualRenewalDate: string;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private routesService: RoutesService,
    public flipperService: FlipperService,
    public billingService: BillingService,
    private appNoticesService: AppNoticesService
  ) {
    this.routes = this.routesService.routes;
  }

  public ngOnInit() {
    if (this.appNotice?.name === 'annual_plans_reminder') {
      this.addAnnualRenewalDate();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get noticeTitle(): string {
    if (this.appNotice?.category === 'general') return '';
    if (this.appNotice?.category === 'integration') {
      return this.appNotice.name === 'failed'
        ? 'Integration failed'
        : 'Integration incomplete';
    }
    return NOTICE_TITLES[this.appNotice?.name] ?? 'Account alert';
  }

  public markViewed() {
    this.appNoticesService.markViewed(this.appNotice.id);
  }

  private addAnnualRenewalDate(): void {
    this.billingService.billingSubscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((subscription) => {
        this.annualRenewalDate = dayjs(subscription.annualRenewalDate).format(
          'MMM DD'
        );
      });
  }
}
